import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Star, Shield, MapPin, Instagram, Facebook, Twitter, Globe, ExternalLink } from 'lucide-react';

export default function ProviderProfilePage() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const { language } = useLanguage();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProvider = useCallback(async () => {
    try {
      const response = await api().get(`/providers/${providerId}/profile`);
      setProvider(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Provider not found' : 'Proveedor no encontrado');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [providerId, api, language, navigate]);

  useEffect(() => {
    fetchProvider();
  }, [fetchProvider]);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  const getCategoryName = (id) => {
    const names = {
      yacht: language === 'en' ? 'Yachts' : 'Yates',
      tour: 'Tours',
      activity: language === 'en' ? 'Activities' : 'Actividades',
      party: language === 'en' ? 'Parties' : 'Fiestas',
      car_rental: language === 'en' ? 'Car Rental' : 'Renta de Autos',
      lodging: language === 'en' ? 'Lodging' : 'Hospedaje'
    };
    return names[id] || id;
  };

  const getLocationName = (id) => {
    const names = {
      playa_del_carmen: 'Playa del Carmen',
      cancun: 'Cancún',
      tulum: 'Tulum',
      quintana_roo: 'Quintana Roo',
      los_cabos: 'Los Cabos'
    };
    return names[id] || id;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  if (!provider) return null;

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4" data-testid="provider-profile-page">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>{language === 'en' ? 'Back' : 'Volver'}</span>
        </button>

        <div className="bg-card rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-caribbean p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-4 border-white/30">
                {provider.profile_photo ? (
                  <img
                    src={getPhotoUrl(provider.profile_photo)}
                    alt={provider.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-heading text-2xl font-bold">
                    {provider.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-xl font-bold">{provider.company_name || provider.name}</h1>
                  {provider.is_verified && (
                    <Shield className="text-gold" size={20} />
                  )}
                </div>
                {provider.company_name && (
                  <p className="text-white/80 text-sm">{provider.name}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Star className="text-gold fill-gold" size={16} />
                  <span className="font-semibold">{provider.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-white/70">({provider.review_count || 0} {language === 'en' ? 'reviews' : 'reseñas'})</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            {(provider.description || provider.description_en) && (
              <div className="space-y-2">
                <h2 className="font-heading font-bold text-lg">{language === 'en' ? 'About' : 'Acerca de'}</h2>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {language === 'en'
                    ? (provider.description_en || provider.description)
                    : (provider.description || provider.description_en)}
                </p>
              </div>
            )}

            {/* Categories */}
            {provider.categories?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">{language === 'en' ? 'Services' : 'Servicios'}</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.categories.map(cat => (
                    <Badge key={cat} variant="outline" className="rounded-full">
                      {getCategoryName(cat)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Locations */}
            {provider.locations?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">{language === 'en' ? 'Locations' : 'Ubicaciones'}</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.locations.map(loc => (
                    <Badge key={loc} variant="secondary" className="rounded-full">
                      <MapPin size={12} className="mr-1" />
                      {getLocationName(loc)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Social Links */}
            {provider.social_links && Object.keys(provider.social_links).length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">{language === 'en' ? 'Social Links' : 'Redes Sociales'}</h3>
                <div className="flex flex-wrap gap-3">
                  {provider.social_links.instagram && (
                    <a
                      href={provider.social_links.instagram.startsWith('http') ? provider.social_links.instagram : `https://${provider.social_links.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-rosa rounded-full text-white text-sm hover:opacity-90 transition-opacity"
                    >
                      <Instagram size={16} />
                      Instagram
                      <ExternalLink size={12} />
                    </a>
                  )}
                  {provider.social_links.facebook && (
                    <a
                      href={provider.social_links.facebook.startsWith('http') ? provider.social_links.facebook : `https://${provider.social_links.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-full text-white text-sm hover:opacity-90 transition-opacity"
                    >
                      <Facebook size={16} />
                      Facebook
                      <ExternalLink size={12} />
                    </a>
                  )}
                  {provider.social_links.twitter && (
                    <a
                      href={provider.social_links.twitter.startsWith('http') ? provider.social_links.twitter : `https://${provider.social_links.twitter}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-sky-500 rounded-full text-white text-sm hover:opacity-90 transition-opacity"
                    >
                      <Twitter size={16} />
                      Twitter
                      <ExternalLink size={12} />
                    </a>
                  )}
                  {provider.social_links.website && (
                    <a
                      href={provider.social_links.website.startsWith('http') ? provider.social_links.website : `https://${provider.social_links.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-caribbean rounded-full text-white text-sm hover:opacity-90 transition-opacity"
                    >
                      <Globe size={16} />
                      Website
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div>
              <h3 className="font-semibold mb-4">{language === 'en' ? 'Recent Reviews' : 'Reseñas Recientes'}</h3>
              {provider.recent_reviews?.length > 0 ? (
                <div className="space-y-4">
                  {provider.recent_reviews.map(review => (
                    <div key={review.id} className="p-4 bg-muted/50 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-caribbean/10 flex items-center justify-center overflow-hidden">
                          {review.tourist_photo ? (
                            <img
                              src={getPhotoUrl(review.tourist_photo)}
                              alt={review.tourist_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="font-heading text-caribbean font-bold">
                              {review.tourist_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{review.tourist_name}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={12}
                                className={i < review.rating ? 'text-gold fill-gold' : 'text-muted'}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {language === 'en' ? 'No reviews yet' : 'Sin reseñas aún'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
